import React, { useEffect, useState } from "react";
import { ReactComponent as CloseIcon } from "../assets/icons/x.svg";
import { LottieLoading } from "./Loading/LottieLoading";
import { api } from "../services/api";

interface PdfFrameProps {
  src: string;
  displayName: string;
  loading?: boolean;
  error?: boolean;
  onClose?: () => void;
  fillContainer?: boolean;
  showHeader?: boolean;
  wrapperClassName?: string;
}

export const PdfFrame: React.FC<PdfFrameProps> = ({
  src,
  displayName,
  loading = false,
  error = false,
  onClose,
  fillContainer = false,
  showHeader = true,
  wrapperClassName = "",
}) => {
  const [iframeError, setIframeError] = useState(false);
  useEffect(() => { setIframeError(false); }, [src]);

  const hasError = error || iframeError;
  const wrapperClass = fillContainer
    ? "flex flex-col flex-1 min-h-0 overflow-hidden bg-white"
    : `flex flex-col p-2 w-fit bg-white lg:min-w-[600px] max-w-[700px] h-[calc(100vh-200px)] rounded-[13px] shadow-[0_4px_33.3px_0_rgba(135,135,135,0.11)] ${wrapperClassName}`;

  const headerClass = fillContainer
    ? "flex justify-between items-center p-4 flex-shrink-0 sticky top-0 z-10 bg-white border-b border-gray2"
    : "flex justify-between items-center p-4 flex-shrink-0";

  return (
    <div className={wrapperClass}>
      {showHeader && (
        <div className={headerClass}>
          <div className="text-black text-base font-medium truncate max-w-[80%]">{displayName}</div>
          <button onClick={onClose} type="button" aria-label="Close">
            <CloseIcon className="w-6 h-6" />
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex-1 flex justify-center items-center min-h-0 [&_svg]:w-10 [&_svg]:h-10">
          <LottieLoading />
        </div>
      ) : hasError ? (
        <div className="flex-1 flex justify-center items-center min-h-0">
          <div className="text-black text-base font-medium">Error loading PDF</div>
        </div>
      ) : src ? (
        <div className="flex-1 min-h-0 overflow-hidden bg-gray-200 rounded-md">
          <iframe
            src={`${src}#toolbar=0&navpanes=0`}
            title="PDF Viewer"
            className="w-full h-full border-none rounded-md"
            loading="lazy"
            onError={() => setIframeError(true)}
          />
        </div>
      ) : null}
    </div>
  );
};

interface PdfViewerProps {
  fileId: string;
  displayName: string;
  sessionId?: string;
  onClose?: () => void;
  fillContainer?: boolean;
  showHeader?: boolean;
  wrapperClassName?: string;
}

const PdfViewer: React.FC<PdfViewerProps> = ({
  fileId,
  displayName,
  sessionId,
  onClose,
  fillContainer = false,
  showHeader = true,
  wrapperClassName = "",
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [signedUrl, setSignedUrl] = useState("");

  useEffect(() => {
    // sessionId can flip from undefined to the real call id shortly after mount
    // (callDetails starts empty and populates async), re-running this effect with
    // a second request in flight. `stale` makes sure a slower, superseded request's
    // result (success or failure) can't overwrite what the newer one already set.
    let stale = false;
    const fetchSignedUrl = async () => {
      try {
        setLoading(true);
        setError(false);
        const response = await api.getSalesAidFileSignedURL(fileId, sessionId);
        if (stale) return;
        setSignedUrl(response.data.signedUrl);
      } catch {
        if (stale) return;
        setError(true);
      } finally {
        if (!stale) setLoading(false);
      }
    };
    fetchSignedUrl();
    return () => {
      stale = true;
    };
  }, [fileId, sessionId]);

  return (
    <PdfFrame
      src={signedUrl}
      displayName={displayName}
      loading={loading}
      error={error}
      onClose={onClose}
      fillContainer={fillContainer}
      showHeader={showHeader}
      wrapperClassName={wrapperClassName}
    />
  );
};

export default PdfViewer;
